import React, { useMemo } from 'react';
import { useTable } from 'react-table';
import { Text } from '@ui/atoms';
import { formatDate, getDecimalNumberWithComma } from '@helpers/miscelleanous';
import useWindowDimensions from '@hooks/useWindowDimensions';

const RepaymentScheduleModal = ({ paymentSchedule }) => {
    const { width } = useWindowDimensions();

    const columns = useMemo(() => {
        return [
            width > 768 && {
                Header: 'No',
                accessor: 'no',
                Footer: () => (
                    <div>
                        <span>Total</span>
                    </div>
                ),
            },
            {
                Header: 'Date',
                accessor: 'date',
                Cell: ({ value }) => (
                    <Text
                        content={value ? formatDate(value, 'DD MMM YYYY') : '-'}
                        className="whitespace-nowrap"
                    />
                ),
            },
            {
                Header: 'Principal',
                accessor: 'principal',
                Cell: ({ value }) => (
                    <Text
                        content={
                            value
                                ? `₹ ${getDecimalNumberWithComma(value, 2)}`
                                : '-'
                        }
                        className="whitespace-nowrap"
                    />
                ),
            },
            {
                Header: 'Interest',
                accessor: 'interest',
                Cell: ({ value }) => (
                    <Text
                        content={
                            value
                                ? `₹ ${getDecimalNumberWithComma(value, 2)}`
                                : '-'
                        }
                        className="whitespace-nowrap"
                    />
                ),
                Footer: () => (
                    <div className="whitespace-nowrap">
                        {`₹ ${getDecimalNumberWithComma(
                            paymentSchedule.reduce(
                                (acc, { principal, interest }) =>
                                    acc + principal + interest,
                                0
                            ),
                            2
                        )}`}
                    </div>
                ),
            },
        ].filter(Boolean);
    }, [width, paymentSchedule]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        rows,
        prepareRow,
    } = useTable({
        columns,
        data: paymentSchedule,
    });

    return (
        <div className="w-full p-4">
            <table
                {...getTableProps()}
                className="w-full overflow-auto bg-gray-100 border border-separate border-gray-300 rounded-xl border-spacing-0"
            >
                <thead>
                    {headerGroups.map((headerGroup, index) => (
                        <tr
                            key={index}
                            {...headerGroup.getHeaderGroupProps()}
                            className="border-b border-gray-300"
                        >
                            {headerGroup.headers.map((column, index) => (
                                <th
                                    key={index}
                                    {...column.getHeaderProps()}
                                    className="p-4 p5-semibold border-b border-gray-300"
                                >
                                    {column.render('Header')}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <tr
                                key={i}
                                {...row.getRowProps()}
                                className="bg-basicWhite"
                            >
                                {row.cells.map((cell, index) => (
                                    <td
                                        key={index}
                                        {...cell.getCellProps()}
                                        className="p-4 p5-regular text-center"
                                    >
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    {footerGroups.map((footerGroup, index) => (
                        <tr key={index} {...footerGroup.getFooterGroupProps()}>
                            {footerGroup.headers.map((column, index) => (
                                <td
                                    key={index}
                                    {...column.getFooterProps()}
                                    className="p-4 p5-semibold text-center"
                                >
                                    {column.render('Footer')}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tfoot>
            </table>
        </div>
    );
};

export default React.memo(RepaymentScheduleModal);
